import { createProgram } from '../utils/webgl.js';
import { eventBus } from '../utils/EventEmitter.js';

/**
 * Manages shader programs and uniforms
 */
export class ShaderManager {
    constructor(gl) {
        this.gl = gl;
        this.programs = new Map();
        this.currentProgram = null;
        this.uniformCache = new Map();
    }

    /**
     * Load and compile a shader program
     * @param {string} name - Program name
     * @param {string} vertexSource - Vertex shader source
     * @param {string} fragmentSource - Fragment shader source
     */
    async loadProgram(name, vertexSource, fragmentSource) {
        try {
            const program = createProgram(this.gl, vertexSource, fragmentSource);
            this.programs.set(name, program);
            
            // Cache uniform locations
            const uniformCount = this.gl.getProgramParameter(program, this.gl.ACTIVE_UNIFORMS);
            const uniforms = new Map();
            
            for (let i = 0; i < uniformCount; i++) {
                const info = this.gl.getActiveUniform(program, i);
                uniforms.set(info.name, this.gl.getUniformLocation(program, info.name));
            }
            
            this.uniformCache.set(program, uniforms);
            
            eventBus.emit('shader:loaded', { name, program });
        } catch (error) {
            console.error(`Failed to load shader program ${name}:`, error);
            eventBus.emit('shader:error', { name, error });
        }
    }

    /**
     * Use a shader program
     * @param {string} name - Program name
     * @returns {WebGLProgram} The shader program
     */
    useProgram(name) {
        const program = this.programs.get(name);
        if (program) {
            this.gl.useProgram(program);
            this.currentProgram = program;
            return program;
        } else {
            throw new Error(`Shader program ${name} not found`);
        }
    }

    /**
     * Set a uniform value
     * @param {string} name - Uniform name
     * @param {any} value - Uniform value
     * @param {string} type - Uniform type (e.g., '1f', '2f', '3f', '4f', 'Matrix4fv')
     */
    setUniform(name, value, type) {
        if (!this.currentProgram) {
            throw new Error('No shader program in use');
        }

        const uniforms = this.uniformCache.get(this.currentProgram);
        const location = uniforms.get(name);

        if (location === undefined) {
            console.warn(`Uniform ${name} not found in current program`);
            return;
        }

        switch (type) {
            case '1f':
                this.gl.uniform1f(location, value);
                break;
            case '2f':
                this.gl.uniform2f(location, value[0], value[1]);
                break;
            case '3f':
                this.gl.uniform3f(location, value[0], value[1], value[2]);
                break;
            case '4f':
                this.gl.uniform4f(location, value[0], value[1], value[2], value[3]);
                break;
            case '1i':
                this.gl.uniform1i(location, value);
                break;
            case 'Matrix4fv':
                this.gl.uniformMatrix4fv(location, false, value);
                break;
            case '2fv':
                this.gl.uniform2fv(location, value);
                break;
            case '3fv':
                this.gl.uniform3fv(location, value);
                break;
            case '4fv':
                this.gl.uniform4fv(location, value);
                break;
            default:
                throw new Error(`Unsupported uniform type: ${type}`);
        }
    }

    /**
     * Set multiple uniforms at once
     * @param {Object} uniforms - Object mapping uniform names to {value, type}
     */
    setUniforms(uniforms) {
        Object.entries(uniforms).forEach(([name, { value, type }]) => {
            this.setUniform(name, value, type);
        });
    }

    /**
     * Delete a shader program
     * @param {string} name - Program name
     */
    deleteProgram(name) {
        const program = this.programs.get(name);
        if (program) {
            this.gl.deleteProgram(program);
            this.programs.delete(name);
            this.uniformCache.delete(program);
        }
    }

    /**
     * Delete all shader programs
     */
    deleteAllPrograms() {
        this.programs.forEach((program) => {
            this.gl.deleteProgram(program);
        });
        this.programs.clear();
        this.uniformCache.clear();
        this.currentProgram = null;
    }

    /**
     * Get a shader program
     * @param {string} name - Program name
     * @returns {WebGLProgram} The shader program
     */
    getProgram(name) {
        return this.programs.get(name);
    }

    /**
     * Get the current shader program
     * @returns {WebGLProgram} The current shader program
     */
    getCurrentProgram() {
        return this.currentProgram;
    }
} 